// File Path: web/workery-frontend/src/pages/Admin/Order/Add/Step2Page.jsx
// UIX Upgraded - Uses WizardFormStep whole page component
// @uix-page: AdminOrderAddStep2Page

import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router";
import {
  useAuthManager,
  useOrderCreationStorage,
} from "../../../../services/Services";
import {
  WizardFormStep,
  FormCard,
  DatePicker,
  UIXThemeProvider,
  useUIXTheme,
} from "../../../../components/UIX";
import {
  PlusCircleIcon,
  ClockIcon,
  HomeModernIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";

// Wizard configuration
const WIZARD_STEPS = [
  { title: "Search" },
  { title: "Details" },
  { title: "Skills" },
  { title: "Review" },
];

// Memoized content component
const Step2Content = memo(function Step2Content() {
  const authManager = useAuthManager();
  const orderCreationStorage = useOrderCreationStorage();
  const navigate = useNavigate();
  const { getThemeClasses } = useUIXTheme();

  // Component states
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);

  // Get existing order state
  const existingOrder = orderCreationStorage.getOrderCreation();

  // Form fields
  const [startDate, setStartDate] = useState(existingOrder?.startDate || "");
  const [isOngoing, setIsOngoing] = useState(existingOrder?.isOngoing || 0);
  const [isHomeSupportService, setIsHomeSupportService] = useState(
    existingOrder?.isHomeSupportService || 0
  );

  // Check authentication and order state
  useEffect(() => {
    if (!authManager.isAuthenticated()) {
      navigate("/login?unauthorized=true");
      return;
    }

    if (!existingOrder || !existingOrder.customerId) {
      navigate("/admin/orders/add/step-1-search");
      return;
    }

    window.scrollTo(0, 0);
  }, [authManager, navigate, existingOrder]);

  // Handle form submission
  const handleNext = useCallback(() => {
    let newErrors = {};
    let hasErrors = false;

    if (isOngoing === 0) {
      newErrors.isOngoing = "Please select if this job is one-time or ongoing";
      hasErrors = true;
    }

    if (isHomeSupportService === 0) {
      newErrors.isHomeSupportService = "Please select if this is a home support service";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      window.scrollTo(0, 0);
      return;
    }

    // Update order state
    const updatedOrder = {
      ...existingOrder,
      startDate: startDate,
      isOngoing: isOngoing,
      isHomeSupportService: isHomeSupportService,
    };

    orderCreationStorage.saveOrderCreation(updatedOrder);
    navigate("/admin/orders/add/step-3");
  }, [isOngoing, isHomeSupportService, existingOrder, startDate, orderCreationStorage, navigate]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setShowCancelWarning(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelWarning(false);
    orderCreationStorage.clearOrderCreation();
    navigate("/admin/orders/add/step-1-search");
  }, [orderCreationStorage, navigate]);

  // Handle back
  const handleBack = useCallback(() => {
    navigate("/admin/orders/add/step-1-search");
  }, [navigate]);

  // Handle date change
  const handleStartDateChange = useCallback((value) => {
    setStartDate(value);
    if (errors.startDate) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.startDate;
        return newErrors;
      });
    }
  }, [errors.startDate]);

  // Handle ongoing change
  const handleOngoingChange = useCallback((value) => {
    setIsOngoing(value);
    if (errors.isOngoing) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.isOngoing;
        return newErrors;
      });
    }
  }, [errors.isOngoing]);

  // Handle home support service change
  const handleHomeSupportChange = useCallback((value) => {
    setIsHomeSupportService(value);
    if (errors.isHomeSupportService) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.isHomeSupportService;
        return newErrors;
      });
    }
  }, [errors.isHomeSupportService]);

  // Action buttons
  const actions = useMemo(() => [
    {
      label: "Cancel",
      variant: "outline",
      onClick: handleCancel,
    },
    {
      label: isLoading ? "Saving..." : "Continue",
      variant: "primary",
      onClick: handleNext,
      disabled: isLoading,
      loading: isLoading,
    },
  ], [handleCancel, handleNext, isLoading]);

  return (
    <>
      <WizardFormStep
        wizardSteps={WIZARD_STEPS}
        currentStep={2}
        wizardTitle="New Order"
        wizardIcon={PlusCircleIcon}
        stepTitle="Job Type Configuration"
        stepSubtitle="Please fill out all the required fields before submitting this form"
        stepIcon={ClockIcon}
        showFormCard={false}
        contentMaxWidth="7xl"
        errors={errors}
        isLoading={isLoading}
        actions={actions}
        onCancel={handleCancel}
        onBack={handleBack}
        actionLayout="end"
      >
        <div className="space-y-8">
          {/* Job Duration Section */}
          <FormCard
            title="Job Duration"
            subtitle="Specify if this is a one-time job or ongoing service"
            icon={ClockIcon}
            maxWidth="7xl"
          >
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Is this job one time or ongoing?
                <span className="text-red-500 ml-1">*</span>
              </label>
              {errors.isOngoing && (
                <p className="text-red-600 text-xs sm:text-sm mb-3">{errors.isOngoing}</p>
              )}
              <div className="space-y-3">
                <label className={`flex items-center p-3 sm:p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-all ${isOngoing === 2 ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="isOngoing"
                    value="2"
                    checked={isOngoing === 2}
                    onChange={() => handleOngoingChange(2)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <span className="block text-sm sm:text-base font-medium text-gray-900">
                      One-Time Job
                    </span>
                    <span className="block text-xs sm:text-sm text-gray-500 mt-0.5">
                      Single service visit with defined completion
                    </span>
                  </div>
                </label>
                <label className={`flex items-center p-3 sm:p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-all ${isOngoing === 1 ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="isOngoing"
                    value="1"
                    checked={isOngoing === 1}
                    onChange={() => handleOngoingChange(1)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <span className="block text-sm sm:text-base font-medium text-gray-900">
                      Ongoing Service
                    </span>
                    <span className="block text-xs sm:text-sm text-gray-500 mt-0.5">
                      Recurring or continuous service arrangement
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </FormCard>

          {/* Home Support Service Section */}
          <FormCard
            title="Service Category"
            subtitle="Identify if this qualifies as a home support service"
            icon={HomeModernIcon}
            maxWidth="7xl"
          >
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Is this job a home support service?
                <span className="text-red-500 ml-1">*</span>
              </label>
              {errors.isHomeSupportService && (
                <p className="text-red-600 text-xs sm:text-sm mb-3">{errors.isHomeSupportService}</p>
              )}
              <div className="space-y-3">
                <label className={`flex items-center p-3 sm:p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-all ${isHomeSupportService === 2 ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="isHomeSupportService"
                    value="2"
                    checked={isHomeSupportService === 2}
                    onChange={() => handleHomeSupportChange(2)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <span className="block text-sm sm:text-base font-medium text-gray-900">
                      No - Regular Service
                    </span>
                    <span className="block text-xs sm:text-sm text-gray-500 mt-0.5">
                      Standard maintenance or repair service
                    </span>
                  </div>
                </label>
                <label className={`flex items-center p-3 sm:p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-all ${isHomeSupportService === 1 ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="isHomeSupportService"
                    value="1"
                    checked={isHomeSupportService === 1}
                    onChange={() => handleHomeSupportChange(1)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div className="ml-3">
                    <span className="block text-sm sm:text-base font-medium text-gray-900">
                      Yes - Home Support Service
                    </span>
                    <span className="block text-xs sm:text-sm text-gray-500 mt-0.5">
                      Qualifies for home support service category
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </FormCard>

          {/* Start Date Section */}
          <FormCard
            title="Schedule Information"
            subtitle="Optional scheduling details for the job"
            icon={CalendarIcon}
            maxWidth="7xl"
          >
            <div className="max-w-xs">
              <DatePicker
                label="When should this job start? (Optional)"
                value={startDate}
                onChange={handleStartDateChange}
                error={errors.startDate}
                helperText="Leave blank if nothing was specified by the client"
                placeholder="Select a date"
                minDate={new Date().toISOString().split("T")[0]}
              />
            </div>
          </FormCard>
        </div>
      </WizardFormStep>

      {/* Cancel Confirmation Modal */}
      {showCancelWarning && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Are you sure?
              </h3>
            </div>
            <div className="px-4 sm:px-6 py-4">
              <p className="text-xs sm:text-sm text-gray-600">
                Your Order record will be cancelled and your work will be lost. This cannot be undone. Do you want to continue?
              </p>
            </div>
            <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 rounded-b-lg">
              <button
                onClick={() => setShowCancelWarning(false)}
                className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                No, Keep Working
              </button>
              <button
                onClick={handleConfirmCancel}
                className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

Step2Content.displayName = 'Step2Content';

function AdminOrderAddStep2Page() {
  return (
    <UIXThemeProvider>
      <Step2Content />
    </UIXThemeProvider>
  );
}

export default AdminOrderAddStep2Page;
